"use client";

import { Scale } from "@/animations/scale";
import aigentFiImg from '@/assets/icons/ic_agent_fi.png';
import Image from '@/components/Image';
import { cn } from "@/utils";
import { formatNumber } from "@/utils/number-format";
import { useResize } from "@/utils/useResize";
import { cva } from "class-variance-authority";
import { useMemo, useState } from "react";
import { useAIAgent } from "../apis/aigentfi";
import { HeaderFinder } from "./header-finder";
import { WrapperFinder } from "./wrapper-finder";
import { AiAgentModal } from "../Menu/modals/ModalAIAgent";

const variants = cva("w-full", {
  variants: {
    twSize: {
      xs: "max-w-[80%] mt-[18px] mr-[14px]",
      sm: "max-w-[80%] mt-[18px] mr-[14px]",
      md: "max-w-[557px] mr-[32px] mt-[32px]",
      lg: "max-w-[557px] mr-[32px] mt-[32px]",
      xl: "max-w-[495px] -mt-[150px] mr-[77px]",
      "2xl": "max-w-[526px] -mt-[159px] mr-[82px]",
      "3xl": "max-w-[557px] -mt-[169px] mr-[87px]",
    },
  },
  defaultVariants: {
    twSize: "2xl",
  },
});

export function FinderAgentFi() {
  const { size, twSize } = useResize();

  const { data } = useAIAgent();
  const [active, setActive] = useState<boolean>(false);
  const deployed = useMemo(() => {
    const value = data?.data?.agents_deployed;
    if (!value) {
      return "--";
    }
    const temp = formatNumber(Number(value));
    return `${temp.value}${temp.text ? temp.text + "+" : ""}`;
  }, [data?.data]);

  const fees = useMemo(() => {
    const value = data?.data?.fees;
    if (!value) {
      return "--";
    }
    const temp = formatNumber(Number(value));
    return `${temp.value}${temp.text ? temp.text + "+" : ""}`;
  }, [data?.data]);

  const total_volume = useMemo(() => {
    const value = data?.data?.total_volume;
    if (!value) {
      return "--";
    }
    const temp = formatNumber(Number(value));
    return `${temp.value}${temp.text ? temp.text + "+" : ""}`;
  }, [data?.data]);

  const users = useMemo(() => {
    const value = data?.data?.users;
    if (!value) {
      return "--";
    }
    const temp = formatNumber(Number(value));
    return `${temp.value}${temp.text ? temp.text + "+" : ""}`;
  }, [data?.data]);

  return (
    <Scale>
      <div className="flex justify-end w-full">
        <WrapperFinder className={cn(variants({ twSize: twSize }), 'self-end')}>
          <HeaderFinder>
            <p className="text-[#23262F] text-center text-[13px] font-medium leading-4 truncate">
              A(i)gentFi
            </p>
          </HeaderFinder>
          <div className="w-full bg-[#09090A] rounded-b-2xl overflow-hidden flex flex-col justify-between">
            <div className="relative bg-[#09090A] flex flex-col md:flex-row ">
              <div className="p-5 mb-[176px] md:mb-0 md:mt-[100px] w-full  md:w-[80%] lg:w-[60%] flex-none relative z-10">
                <p className="">
                  <span className="text-wrap text-2xl leading-8 font-medium text-white mr-3">
                    A(i)gentFi - AI Agent
                  </span>
                  <span className="bg-[#2DEB46] text-[#054A05] text-sm leading-4 font-medium px-[10px] py-[2px] rounded-full">
                    NEW
                  </span>
                </p>

                <div className="grid grid-cols-2  sm:max-w-[300px] mt-8">
                  <div className="flex flex-col gap-1 justify-start items-start gap-x-3 gap-y-1">
                    <p className="text-2xl leading-7 text-white font-medium">
                      {deployed}
                    </p>
                    <p className="text-white/65 text-sm leading-6 font-medium">
                      Agents deployed
                    </p>
                  </div>
                  <div className="flex flex-col gap-1 justify-start items-start">
                    <p className="text-2xl leading-7 text-white font-medium">
                      ${total_volume}
                    </p>
                    <p className="text-white/65 text-sm leading-6 font-medium">
                      Total volume
                    </p>
                  </div>
                  <div className="flex flex-col gap-1 justify-start items-start">
                    <p className="text-2xl leading-7 text-white font-medium">
                      {users}
                    </p>
                    <p className="text-white/65 text-sm leading-6 font-medium">
                      Total users
                    </p>
                  </div>
                  <div className="flex flex-col gap-1 justify-start items-start">
                    <p className="text-2xl leading-7 text-white font-medium">
                      ${fees}
                    </p>
                    <p className="text-white/65 text-sm leading-6 font-medium">
                      Total fee
                    </p>
                  </div>
                </div>
              </div>
              <div className="">
                <div className="absolute md:top-0 bottom-0 right-0 left-0 md:left-auto">
                  {size === 'mobile' ? (
                    <Image
                      src={require('@/assets/bg/bg_agent_fi_mobile.png')}
                      alt="agent"
                      className="w-full object-contain"
                    />
                  ) : (
                    <Image
                      src={require('@/assets/bg/bg_agent_fi.png')}
                      alt="agent"
                      className="h-full object-contain"
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-[40px_1fr_114px] lg:grid-cols-[48px_1fr_114px] gap-2.5 lg:gap-4 p-4  bg-black/10  border-t border-t-white border-opacity-[0.12]">
              <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl flex-shrink-0">
                <Image
                  src={aigentFiImg}
                  width={512}
                  height={512}
                  alt="DeFutures"
                />
              </div>

              <div className="flex flex-col justify-center">
                <p className="text-sm font-medium text-white line-clamp-1">
                  A(i)gentFi
                </p>
                <p className="text-sm font-medium text-white/65 line-clamp-1">
                  Pioneering AI launchpad on ZKsync
                </p>
              </div>
              <div className="flex items-center justify-center">
                <button
                  type="button"
                  className="text-[#141416] text-center text-sm left-5 font-medium py-[10px] px-6 rounded-full bg-[#fff] cursor-pointer"
                  onClick={() => {
                    setActive(true);
                  }}
                >
                  View More
                </button>
              </div>
            </div>
          </div>
        </WrapperFinder>
        {active ? (
          <AiAgentModal
            onClose={() => {
              setActive(false);
            }}
          />
        ) : null}
      </div>
    </Scale>
  );
}
